import { Controller } from "@hotwired/stimulus"

// Zellen innerhalb .dynamic-filter-segments (eine zusammenhängende Leiste, siehe Partial)
const DF_CELL =
  "dynamic-filter-value-wrap flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch"
const DF_CTL =
  "block w-full h-10 text-xs px-2 text-gray-800 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:ring-opacity-40 placeholder-gray-400"

// Baut beim Absenden aus Zeilen (Feld + Bedingung + Wert) die Ransack-Parameter q[attr_pred]=value.
export default class extends Controller {
  static targets = ["form", "rows", "fieldSelect", "predSelect", "valueWrap", "catalog", "catalogSearch"]
  static values = { filterable: Array }

  connect() {
    this._onClickOutsideCatalog = (e) => {
      if (this.hasCatalogTarget && !this.catalogTarget.closest(".dynamic-filter-catalog-wrap")?.contains(e.target)) {
        this.closeCatalog()
      }
    }
    document.addEventListener("click", this._onClickOutsideCatalog)

    this.predicatesByType = {
      string: [["enthält", "cont"], ["ist gleich", "eq"], ["beginnt mit", "start"], ["endet mit", "end"], ["ist nicht gleich", "not_eq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      integer: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      decimal: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      boolean: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      date: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]],
      datetime: [["ist gleich", "eq"], ["ist nicht gleich", "not_eq"], ["größer als", "gt"], ["größer oder gleich", "gteq"], ["kleiner als", "lt"], ["kleiner oder gleich", "lteq"], ["ist ausgefüllt", "present"], ["ist leer", "blank"]]
    }

    if (this.hasRowsTarget) {
      this.rowsTarget.querySelectorAll(".dynamic-filter-row").forEach((row) => {
        this.updateValueControl(row)
        this.updateProgressiveUI(row)
      })
    }
  }

  onFieldChange(event) {
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    this.rebuildPredicates(row, { resetPred: true })
    this.updateValueControl(row)
    this.updateProgressiveUI(row)
  }

  onPredicateChange(event) {
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    this.updateValueControl(row)
    this.updateProgressiveUI(row)
    this.autoSubmitIfComplete(row)
  }

  // Auto-Submit: wenn Feld + Predicate + Wert komplett (oder present/blank ohne Wert)
  autoSubmitIfComplete(row) {
    const attr = this.getAttrFromRow(row)
    if (!attr) return
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    const pred = (predSelect && predSelect.value) || ""
    if (!pred) return
    if (["present", "blank"].includes(pred)) {
      this.submitForm(new Event("auto"))
      return
    }
    const valueInput = row.querySelector("[data-dynamic-filters-target=valueInput]")
    const val = (valueInput && valueInput.value) || ""
    if (val.trim()) {
      this.submitForm(new Event("auto"))
    }
  }

  // Konvertiert Dynamic-Filter-Felder und submittet das Form
  submitAndNavigate(event) {
    if (event) event.preventDefault()
    this.submitForm(new Event("auto"))
  }

  // Debounced auto-submit bei Wert-Eingabe (nach 800ms Pause)
  debouncedAutoSubmit(event) {
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    clearTimeout(this._autoSubmitTimer)
    this._autoSubmitTimer = setTimeout(() => {
      this.autoSubmitIfComplete(row)
    }, 800)
  }

  /** Schrittweise Sichtbarkeit: Bedingung erst nach Feld, Wert erst nach Bedingung (außer present/blank). */
  updateProgressiveUI(row) {
    const attr = this.getAttrFromRow(row)
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    const pred = (predSelect && predSelect.value) || ""
    const predWrap = row.querySelector('[data-dynamic-filter-step="pred"]')
    const valueWrap = row.querySelector('[data-dynamic-filter-step="value"]')

    if (predWrap) predWrap.classList.toggle("hidden", !attr)

    const showValue = !!(attr && pred && !["present", "blank"].includes(pred))
    if (valueWrap) valueWrap.classList.toggle("hidden", !showValue)
  }

  getAttrFromRow(row) {
    const fieldSelect = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
    if (!fieldSelect) return ""
    if (fieldSelect.tomselect) {
      const v = fieldSelect.tomselect.getValue()
      return typeof v === "string" ? v : Array.isArray(v) ? v[0] || "" : ""
    }
    return fieldSelect.value || ""
  }

  conditionPlaceholder() {
    return this.formTarget?.dataset?.selectCondition || ""
  }

  rebuildPredicates(row, { resetPred = false } = {}) {
    const fieldSelect = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    if (!fieldSelect || !predSelect || !this.filterableValue.length) return

    const attr = this.getAttrFromRow(row)
    const ph = this.conditionPlaceholder()

    if (!attr) {
      predSelect.innerHTML = ""
      const o = document.createElement("option")
      o.value = ""
      o.textContent = ph
      o.selected = true
      predSelect.appendChild(o)
      return
    }

    const oldPred = resetPred ? "" : predSelect.value
    const fa = this.filterableValue.find((a) => a.ransack_attribute === attr)
    const type = (fa && fa.type) || "string"
    const preds = this.predicatesByType[type] || this.predicatesByType.string

    predSelect.innerHTML = ""
    const phOpt = document.createElement("option")
    phOpt.value = ""
    phOpt.textContent = ph
    predSelect.appendChild(phOpt)
    preds.forEach(([label, val]) => {
      const o = document.createElement("option")
      o.value = val
      o.textContent = label
      predSelect.appendChild(o)
    })

    if (resetPred) {
      predSelect.value = ""
      return
    }

    const stillValid = oldPred && preds.some(([, v]) => v === oldPred)
    predSelect.value = stillValid ? oldPred : ""
  }

  rowValIndex(fieldSelect) {
    const m = fieldSelect?.name?.match(/__attr_(\d+)/)
    return m ? m[1] : "0"
  }

  currentValueFromRow(row) {
    const el = row.querySelector("[data-dynamic-filters-target=valueInput]")
    if (!el) return ""
    if (el.tagName === "SELECT") return el.value
    return el.value || ""
  }

  updateValueControl(row) {
    const fieldSelect = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
    const predSelect = row.querySelector("[data-dynamic-filters-target=predSelect]")
    const valueWrap = row.querySelector("[data-dynamic-filters-target=valueWrap]")
    const valueSlot = valueWrap?.querySelector("[data-dynamic-filter-value-slot]")
    if (!fieldSelect || !predSelect || !valueWrap || !valueSlot) return

    const attr = this.getAttrFromRow(row)
    const pred = predSelect.value
    const idx = this.rowValIndex(fieldSelect)
    const name = `q[__val_${idx}]`

    const needsValue = !!(attr && pred && !["present", "blank"].includes(pred))

    const preserved = this.currentValueFromRow(row)
    valueSlot.innerHTML = ""
    if (!needsValue) {
      return
    }
    const fa = attr ? this.filterableValue.find((a) => a.ransack_attribute === attr) : null
    const type = (fa && fa.type) || "string"
    const cpOptions = fa && Array.isArray(fa.options) && fa.options.length > 0 ? fa.options : null

    const valueAria = this.formTarget?.dataset?.valueLabel || ""
    const valuePh = this.formTarget?.dataset?.valuePlaceholder || ""

    let control
    if (cpOptions) {
      valueWrap.className = `${DF_CELL} min-w-24 w-32 max-w-xs sm:w-36 sm:max-w-sm`
      control = document.createElement("select")
      control.className = `${DF_CTL} truncate`
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      const empty = document.createElement("option")
      empty.value = ""
      empty.textContent = ""
      control.appendChild(empty)
      cpOptions.forEach((item) => {
        const val = typeof item === "object" && item.value !== undefined ? String(item.value) : String(item)
        const label = typeof item === "object" && item.label !== undefined ? String(item.label) : val
        const o = document.createElement("option")
        o.value = val
        o.textContent = label
        if (val === preserved) o.selected = true
        control.appendChild(o)
      })
    } else if (type === "boolean" && ["eq", "not_eq"].includes(pred)) {
      valueWrap.className = `${DF_CELL} w-24 min-w-16 max-w-xs sm:w-28 sm:max-w-sm`
      control = document.createElement("select")
      control.className = `${DF_CTL}`
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      const yesLabel = this.formTarget?.dataset?.yesText || "true"
      const noLabel = this.formTarget?.dataset?.noText || "false"
      ;[
        ["", ""],
        [yesLabel, "true"],
        [noLabel, "false"]
      ].forEach(([label, val]) => {
        const o = document.createElement("option")
        o.value = val
        o.textContent = label
        if (
          val !== "" &&
          (val === preserved ||
            (val === "true" && (preserved === "1" || preserved === "true")) ||
            (val === "false" && (preserved === "0" || preserved === "false")))
        ) {
          o.selected = true
        }
        control.appendChild(o)
      })
    } else if (type === "date") {
      valueWrap.className = `${DF_CELL} w-44 max-w-sm sm:w-52`
      // Select mit Datums-Presets + benutzerdefiniertem Datum
      const presets = this.datePresetsForPredicate(pred)
      const container = document.createElement("div")
      container.className = "flex items-stretch gap-0"

      const select = document.createElement("select")
      select.className = `${DF_CTL} truncate`
      select.name = name
      select.setAttribute("data-dynamic-filters-target", "valueInput")
      if (valueAria) select.setAttribute("aria-label", valueAria)

      const emptyOpt = document.createElement("option")
      emptyOpt.value = ""
      emptyOpt.textContent = "Datum wählen…"
      select.appendChild(emptyOpt)

      presets.forEach(({ label, value }) => {
        const o = document.createElement("option")
        o.value = value
        o.textContent = label
        if (value === preserved) o.selected = true
        select.appendChild(o)
      })

      // "Benutzerdefiniert" Option wenn preserved nicht in Presets
      const isCustom = preserved && !presets.some((p) => p.value === preserved)
      const customOpt = document.createElement("option")
      customOpt.value = "__custom__"
      customOpt.textContent = "📅 Benutzerdefiniert…"
      if (isCustom) {
        customOpt.value = preserved
        customOpt.textContent = `📅 ${preserved}`
        customOpt.selected = true
      }
      select.appendChild(customOpt)

      select.addEventListener("change", () => {
        if (select.value === "__custom__") {
          const dateInput = document.createElement("input")
          dateInput.type = "date"
          dateInput.className = DF_CTL
          dateInput.name = name
          dateInput.setAttribute("data-dynamic-filters-target", "valueInput")
          dateInput.setAttribute("data-action", "change->dynamic-filters#submitAndNavigate")
          select.replaceWith(dateInput)
          dateInput.focus()
          dateInput.showPicker?.()
        }
      })

      container.appendChild(select)
      control = container
      // Auto-submit bei Select-Change wird unten gesetzt
      select.setAttribute("data-action", "change->dynamic-filters#submitAndNavigate")
    } else if (type === "datetime") {
      valueWrap.className = `${DF_CELL} w-44 max-w-sm sm:w-52 sm:max-w-md`
      control = document.createElement("input")
      control.type = "datetime-local"
      control.className = DF_CTL
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = this.normalizeDatetimeLocalValue(preserved)
    } else if (type === "integer") {
      valueWrap.className = `${DF_CELL} w-20 max-w-xs sm:w-24 sm:max-w-sm`
      control = document.createElement("input")
      control.type = "number"
      control.step = "1"
      control.className = DF_CTL
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = preserved
    } else if (type === "decimal") {
      valueWrap.className = `${DF_CELL} w-20 max-w-xs sm:w-24 sm:max-w-sm`
      control = document.createElement("input")
      control.type = "text"
      control.inputMode = "decimal"
      control.className = DF_CTL
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = preserved
      control.placeholder = valuePh
    } else {
      valueWrap.className = `${DF_CELL} w-24 min-w-16 max-w-xs sm:w-28 sm:max-w-sm`
      control = document.createElement("input")
      control.type = "text"
      control.className = DF_CTL
      if (valueAria) control.setAttribute("aria-label", valueAria)
      control.name = name
      control.setAttribute("data-dynamic-filters-target", "valueInput")
      control.value = preserved
      control.placeholder = valuePh
    }

    // Auto-submit Actions je nach Control-Typ
    if (control.tagName === "SELECT" || control.type === "date" || control.type === "datetime-local") {
      control.setAttribute("data-action", "change->dynamic-filters#submitAndNavigate")
    } else if (control.tagName === "INPUT" && ["text", "number"].includes(control.type)) {
      control.setAttribute("data-action", "keydown.enter->dynamic-filters#submitAndNavigate input->dynamic-filters#debouncedAutoSubmit")
    }

    valueSlot.appendChild(control)
  }

  datePresetsForPredicate(pred) {
    const today = new Date()
    const fmt = (d) => d.toISOString().slice(0, 10)
    const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r }
    const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
    const startOfYear = (d) => new Date(d.getFullYear(), 0, 1)
    const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)

    if (["gteq", "gt"].includes(pred)) {
      // "seit / ab" → Startdaten in der Vergangenheit
      return [
        { label: "Heute", value: fmt(today) },
        { label: "Letzte 7 Tage", value: fmt(addDays(today, -7)) },
        { label: "Letzte 30 Tage", value: fmt(addDays(today, -30)) },
        { label: "Letzte 90 Tage", value: fmt(addDays(today, -90)) },
        { label: "Seit Monatsanfang", value: fmt(startOfMonth(today)) },
        { label: "Seit Jahresanfang", value: fmt(startOfYear(today)) },
      ]
    }
    if (["lteq", "lt"].includes(pred)) {
      // "bis" → Enddaten
      return [
        { label: "Heute", value: fmt(today) },
        { label: "Gestern", value: fmt(addDays(today, -1)) },
        { label: "Monatsende", value: fmt(endOfMonth(today)) },
        { label: "Vor 7 Tagen", value: fmt(addDays(today, -7)) },
        { label: "Vor 30 Tagen", value: fmt(addDays(today, -30)) },
      ]
    }
    // eq, not_eq
    return [
      { label: "Heute", value: fmt(today) },
      { label: "Gestern", value: fmt(addDays(today, -1)) },
      { label: "Vor 7 Tagen", value: fmt(addDays(today, -7)) },
      { label: "Vor 30 Tagen", value: fmt(addDays(today, -30)) },
    ]
  }

  normalizeDateValue(val) {
    if (!val) return ""
    const s = String(val).trim()
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
    const d = s.match(/^(\d{2})\.(\d{2})\.(\d{4})$/)
    if (d) return `${d[3]}-${d[2]}-${d[1]}`
    return s.slice(0, 10)
  }

  normalizeDatetimeLocalValue(val) {
    if (!val) return ""
    let s = String(val).trim().replace(" ", "T")
    if (s.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) return s.slice(0, 16)
    return ""
  }

  // --- Katalog-Dropdown ---

  toggleCatalog(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasCatalogTarget) return
    const isOpen = !this.catalogTarget.classList.contains("hidden")
    if (isOpen) {
      this.closeCatalog()
    } else {
      this.renderCatalog()
      this.catalogTarget.classList.remove("hidden")
      if (this.hasCatalogSearchTarget) {
        this.catalogSearchTarget.value = ""
        this.catalogSearchTarget.focus()
      }
    }
  }

  closeCatalog() {
    if (this.hasCatalogTarget) this.catalogTarget.classList.add("hidden")
  }

  filterCatalog(event) {
    const q = (event.target.value || "").toLowerCase()
    this.catalogTarget.querySelectorAll("[data-catalog-item]").forEach((item) => {
      const label = (item.dataset.catalogLabel || "").toLowerCase()
      item.classList.toggle("hidden", q.length > 0 && !label.includes(q))
    })
  }

  renderCatalog() {
    if (!this.hasCatalogTarget) return
    const list = this.catalogTarget.querySelector("[data-catalog-list]")
    if (!list) return

    // Bereits verwendete Attribute ermitteln
    const usedAttrs = new Set()
    if (this.hasRowsTarget) {
      this.rowsTarget.querySelectorAll(".dynamic-filter-row").forEach((row) => {
        const attr = this.getAttrFromRow(row)
        if (attr) usedAttrs.add(attr)
      })
    }

    list.innerHTML = ""
    // Sektions-Header
    const header = document.createElement("div")
    header.className = "px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-gray-400 select-none"
    header.textContent = "Filter auswählen"
    list.appendChild(header)

    this.filterableValue.forEach((fa) => {
      const used = usedAttrs.has(fa.ransack_attribute)
      const item = document.createElement("button")
      item.type = "button"
      item.dataset.catalogItem = ""
      item.dataset.catalogLabel = fa.label
      item.dataset.catalogAttr = fa.ransack_attribute
      item.className = used
        ? "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-xs opacity-40 cursor-not-allowed"
        : "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-xs text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer transition-colors"
      item.disabled = used

      const icon = fa.fa_icon || "font"
      item.innerHTML =
        `<i class="fas fa-${icon} w-4 shrink-0 text-center text-gray-400" aria-hidden="true"></i>` +
        `<span class="flex-1 truncate">${this.escapeHtml(fa.label)}</span>` +
        (used ? `<span class="text-xs text-gray-400">aktiv</span>` : "")

      if (!used) {
        item.addEventListener("click", (e) => {
          e.stopPropagation()
          e.preventDefault()
          this.closeCatalog()
          this.addRowWithField(fa.ransack_attribute)
        })
      }
      list.appendChild(item)
    })
  }

  /** Baut eine Filter-Row programmatisch (Fallback wenn kein Template im DOM). */
  buildRowElement(preselectedAttr) {
    if (!this.filterableValue.length) return null
    const idx = 0
    const ph = this.conditionPlaceholder()
    const valuePh = this.formTarget?.dataset?.valuePlaceholder || ""

    const row = document.createElement("div")
    row.className = "dynamic-filter-row inline-flex max-w-full shrink-0 flex-nowrap items-stretch"

    const segments = document.createElement("div")
    segments.className = "dynamic-filter-segments inline-flex max-w-full flex-nowrap items-stretch overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm divide-x divide-gray-100 transition-shadow hover:shadow-md"

    // Feld-Select
    const fieldWrap = document.createElement("div")
    fieldWrap.className = "flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch relative w-40 min-w-24 sm:w-48"
    const fieldSelect = document.createElement("select")
    fieldSelect.name = `q[__attr_${idx}]`
    fieldSelect.setAttribute("data-dynamic-filters-target", "fieldSelect")
    fieldSelect.setAttribute("data-action", "change->dynamic-filters#onFieldChange")
    fieldSelect.className = "block w-full h-10 text-xs px-2 text-gray-800 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:ring-opacity-40 placeholder-gray-400"
    const emptyOpt = document.createElement("option")
    emptyOpt.value = ""
    emptyOpt.textContent = this.formTarget?.dataset?.valuePlaceholder || "Feld"
    fieldSelect.appendChild(emptyOpt)
    this.filterableValue.forEach((fa) => {
      const o = document.createElement("option")
      o.value = fa.ransack_attribute
      o.textContent = fa.label
      if (preselectedAttr && fa.ransack_attribute === preselectedAttr) o.selected = true
      fieldSelect.appendChild(o)
    })
    fieldWrap.appendChild(fieldSelect)
    segments.appendChild(fieldWrap)

    // Predicate-Select
    const predWrap = document.createElement("div")
    predWrap.className = "dynamic-filter-pred-wrap shrink-0"
    predWrap.setAttribute("data-dynamic-filter-step", "pred")
    const predInner = document.createElement("div")
    predInner.className = "flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch w-28 min-w-28 sm:w-32"
    const predSelect = document.createElement("select")
    predSelect.name = `q[__pred_${idx}]`
    predSelect.className = "block w-full h-10 text-xs px-2 text-gray-800 border-0 bg-transparent focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 focus:ring-opacity-40 placeholder-gray-400"
    predSelect.setAttribute("data-dynamic-filters-target", "predSelect")
    predSelect.setAttribute("data-action", "change->dynamic-filters#onPredicateChange")
    const predPh = document.createElement("option")
    predPh.value = ""
    predPh.textContent = ph
    predSelect.appendChild(predPh)
    predInner.appendChild(predSelect)
    predWrap.appendChild(predInner)
    segments.appendChild(predWrap)

    // Value
    const valWrap = document.createElement("div")
    valWrap.className = "dynamic-filter-value-wrap flex min-h-10 min-w-0 flex-shrink-0 overflow-hidden bg-white items-stretch w-24 min-w-16 max-w-xs sm:w-28 sm:max-w-sm hidden"
    valWrap.setAttribute("data-dynamic-filter-step", "value")
    valWrap.setAttribute("data-dynamic-filters-target", "valueWrap")
    const valSlot = document.createElement("div")
    valSlot.className = "flex min-h-10 min-w-0 flex-1 items-stretch"
    valSlot.setAttribute("data-dynamic-filter-value-slot", "")
    valWrap.appendChild(valSlot)
    segments.appendChild(valWrap)

    // Remove button
    const removeBtn = document.createElement("button")
    removeBtn.type = "button"
    removeBtn.className = "flex min-h-10 w-8 shrink-0 items-center justify-center border-0 bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-red-400 focus:ring-opacity-40"
    removeBtn.setAttribute("data-action", "click->dynamic-filters#removeRow")
    removeBtn.innerHTML = '<i class="fas fa-times text-xs" aria-hidden="true"></i>'
    segments.appendChild(removeBtn)

    row.appendChild(segments)
    return row
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }

  addRowWithField(attr) {
    // Wenn kein rows-Target vorhanden, dynamisch einen Container anlegen
    if (!this.hasRowsTarget) {
      const container = this.element.querySelector(".dynamic-filters-inline") || this.element
      const rowsDiv = document.createElement("div")
      rowsDiv.className = "flex min-w-0 flex-1 flex-wrap items-center gap-x-1.5 gap-y-2 mt-2"
      rowsDiv.setAttribute("data-dynamic-filters-target", "rows")
      container.appendChild(rowsDiv)
    }
    const rows = this.rowsTarget
    const tpl = document.getElementById("dynamic-filter-row-template")
    let rowEl
    if (tpl?.content) {
      rowEl = document.importNode(tpl.content, true).querySelector(".dynamic-filter-row")
    } else {
      rowEl = rows.querySelector(".dynamic-filter-row")?.cloneNode(true)
    }
    if (!rowEl) {
      // Kein Template vorhanden — Row programmatisch bauen
      rowEl = this.buildRowElement(attr)
      if (!rowEl) return
    }
    const idx = rows.querySelectorAll(".dynamic-filter-row").length
    rowEl.querySelectorAll("[name^='q[__']").forEach((el) => {
      el.name = el.name.replace(/\d+/, idx)
    })
    const fieldSelect = rowEl.querySelector("[data-dynamic-filters-target=fieldSelect]")
    const valInput = rowEl.querySelector("[data-dynamic-filters-target=valueInput]")
    if (valInput) valInput.value = ""
    rows.appendChild(rowEl)

    // Feld vorauswählen + ersten Predicate auto-setzen
    if (fieldSelect && attr) {
      requestAnimationFrame(() => {
        if (fieldSelect.tomselect) {
          fieldSelect.tomselect.setValue(attr, true)
        } else {
          fieldSelect.value = attr
        }
        this.rebuildPredicates(rowEl, { resetPred: true })

        // Auto-select ersten Predicate (z.B. "enthält" für String, "ist gleich" für Select/Bool)
        const predSelect = rowEl.querySelector("[data-dynamic-filters-target=predSelect]")
        if (predSelect && predSelect.options.length > 1) {
          predSelect.selectedIndex = 1 // Erste Option nach Placeholder
        }

        this.updateValueControl(rowEl)
        this.updateProgressiveUI(rowEl)

        // Value-Input fokussieren damit der User sofort tippen kann
        requestAnimationFrame(() => {
          const valInput = rowEl.querySelector("[data-dynamic-filters-target=valueInput]")
          if (valInput && !valInput.disabled) valInput.focus()
        })
      })
    } else if (fieldSelect) {
      fieldSelect.value = ""
      this.rebuildPredicates(rowEl, { resetPred: true })
      this.updateValueControl(rowEl)
      this.updateProgressiveUI(rowEl)
    }
  }

  addRow(event) {
    event.preventDefault()
    // Wenn Katalog verfügbar ist, öffne den Katalog statt direkt eine leere Row anzulegen
    if (this.hasCatalogTarget) {
      this.toggleCatalog(event)
      return
    }
    this.addRowWithField("")
  }

  disconnect() {
    if (this._onClickOutsideCatalog) {
      document.removeEventListener("click", this._onClickOutsideCatalog)
    }
  }

  removeRow(event) {
    if (!this.hasRowsTarget) return
    const row = event.target.closest(".dynamic-filter-row")
    if (!row) return
    row.remove()
    // Auto-submit damit der entfernte Filter auch serverseitig verschwindet (Chip + Ergebnisse)
    this.submitForm(new Event("auto"))
  }

  submitForm(event) {
    const form = this.formTarget
    const rows = form.querySelectorAll(".dynamic-filter-row")
    const keysToReplace = new Set()
    const hiddenPayload = []

    // Zuerst Werte lesen — Felder heißen q[__attr_N] / q[__pred_N] / q[__val_N] und würden
    // beim vorzeitigen remove() aus dem DOM verschwinden (querySelector in der Zeile findet sie nicht mehr).
    rows.forEach((row) => {
      const fieldEl = row.querySelector("[data-dynamic-filters-target=fieldSelect]")
      let attr = ((fieldEl && fieldEl.value) || "").trim()
      if (!attr && fieldEl && fieldEl.tomselect) {
        const v = fieldEl.tomselect.getValue()
        attr = (typeof v === "string" ? v : Array.isArray(v) ? v[0] || "" : "").trim()
      }
      const pred = (row.querySelector("[data-dynamic-filters-target=predSelect]")?.value || "").trim()
      const valEl = row.querySelector("[data-dynamic-filters-target=valueInput]")
      let val = ""
      if (valEl) {
        val = (valEl.tagName === "SELECT" ? valEl.value : valEl.value || "").trim()
      }
      if (!attr || !pred) return
      keysToReplace.add(`${attr}_${pred}`)
      if (["present", "blank"].includes(pred) || val !== "") {
        hiddenPayload.push({
          name: `q[${attr}_${pred}]`,
          value: ["present", "blank"].includes(pred) ? "1" : val
        })
      }
    })

    form.querySelectorAll("input, select").forEach((el) => {
      const n = el.getAttribute("name") || ""
      if (n.startsWith("q[__")) el.remove()
    })

    hiddenPayload.forEach(({ name, value }) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      form.appendChild(input)
    })
    form.querySelectorAll("input[name^='q['][data-dynamic-filters-keep]").forEach((el) => {
      const match = el.name.match(/^q\[(.+)\]$/)
      if (match && keysToReplace.has(match[1])) el.remove()
    })

    // Bei programmatischem Aufruf (auto-submit) das Form direkt absenden.
    // form.submit() statt requestSubmit() — umgeht den submit-Event-Handler
    // (sonst doppelter submitForm-Aufruf → Felder werden nochmal konvertiert → leer).
    if (event && event.type === "auto") {
      form.submit()
    }
  }
}
